import { Component, inject, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
import { of } from 'rxjs';
import { Meta, Title } from '@angular/platform-browser';
import { TransferState } from '@angular/core';
import { HostConfigService } from '@/common/host-config/host-config.service';
import { IMAGES_KEY } from '@/main/main-guard/main.guard';
import { SEOService } from '@/common/seo.service';
import { CurrentOfferFacade } from '@/_store/current-offer/current-offer.facade';
import { isPlatformServer } from '@angular/common';
import { GeneralDataFacade } from '@/_store/general/general.facade';
import { SectionWWW, SectionWWWKinds } from '@/_store/general/general.types';

@Component({
	selector: 'app-main',
	templateUrl: './main.component.html',
})
export class MainComponent implements OnInit, OnDestroy {
	readonly SectionWWWKinds = SectionWWWKinds;
	readonly sectionWWW$ = of<SectionWWW[]>([]);
	readonly hostConfig = inject(HostConfigService);
	private readonly platformId = inject(PLATFORM_ID);
	private readonly meta = inject(Meta);
	private readonly title = inject(Title);
	private readonly transferState = inject(TransferState);
	private readonly seoService = inject(SEOService);
	private readonly currentOfferFacade = inject(CurrentOfferFacade);
	private readonly generalDataFacade = inject(GeneralDataFacade);

	constructor() {
		this.sectionWWW$ = this.generalDataFacade.sectionWWW$;
	}

	ngOnInit(): void {
		const images = this.transferState.get(IMAGES_KEY, null);
		if (images) {
			images.forEach((image) => {
				const url = new URL(image.multimedia.url);
				this.seoService.setPreconnectLink(url.origin);
			});
		}
		const pageTitle = this.hostConfig.defaultTitle();
		const pageDescription = this.hostConfig.defaultDescription();
		const baseUrl = this.seoService.getBaseUrl();
		const domain = baseUrl.replace(/^https?:\/\//, '').replace(/\/$/, '');

		this.meta.addTags(
			[
				{
					name: 'description',
					content: pageDescription,
				},
				{
					property: 'og:url',
					content: baseUrl,
				},
				{
					property: 'og:type',
					content: 'website',
				},
				{
					property: 'og:title',
					content: pageTitle,
				},
				{
					property: 'og:description',
					content: pageDescription,
				},
				{
					name: 'twitter:card',
					content: 'summary_large_image',
				},
				{
					name: 'twitter:domain',
					content: domain,
				},
				{
					name: 'twitter:url',
					content: baseUrl,
				},
				{
					name: 'twitter:title',
					content: pageTitle,
				},
				{
					name: 'twitter:description',
					content: pageDescription,
				},
			],
			false,
		);

		this.title.setTitle(pageTitle);
		this.generalDataFacade.fetchSectionWWW();

		this.currentOfferFacade.clearCurrentOffer();

		this.setCanonicalLink();
	}

	ngOnDestroy(): void {
		this.seoService.removePreconnectLinks();
	}

	private setCanonicalLink(): void {
		if (isPlatformServer(this.platformId)) {
			return;
		}

		const index = window.location.href.indexOf('?');
		this.seoService.setCanonicalLink(window.location.href.slice(0, index), { withOrigin: true });
	}
}
